import { logFirebaseUser } from './firebaseUtils';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import { Auth0ContextInterface } from '@auth0/auth0-react';

const USER_URL = process.env.REACT_APP_USER_URL || '';
const LOADBOARD_URL = process.env.REACT_APP_LOADBOARD_URL || '';
const RTSPRO_URL = process.env.REACT_APP_RTSPRO_URL || '';

export const LOADBOARD_PERMISSION = 'ryantrans.Loadboard';
export const BOOKIT_NOW_PERMISSION = 'ryantrans.LoadboardBook';

const FREIGHT_VIEW_ACCESS_PERMISSION_LEVEL = 'Freight - View Loads';
const FREIGHT_FULL_ACCESS_PERMISSION_LEVEL = 'Freight - Full Access';

export interface UserInfo {
  token: string;
  user: string;
  id: string;
}

export interface CurrentUser {
  company: string;
  createdOn: string;
  crmId: string;
  deviceIds: string[];
  email: string;
  factoring_status: string;
  firstname?: string;
  lastname: string;
  note: string;
  permissions?: { name: string; status: string }[];
  pin: string;
  roles: string[];
  status: string;
  user_type: string;
  _id: string;
  password: string;
  phone?: string;
  mcleod_customer_id: string[];
  bridge_id: string[];
  device_ids: string[];
  mc_number?: string;
  metaData?: UserMetadataI;
  mcleod_carrier_id: string;
  dot_number?: string;
}

export type UserMetadata = {
  metaData?: UserMetadataI;
};

interface NotificationMessage {
  congratulations?: boolean;
  remember?: boolean;
  seenNotificationsSettings?: boolean;
}
export interface UserMetadataI {
  seenJoyrides?: { freightLoadNumberSearchSeen?: boolean };
  rtsProMyLanesJoyRideSeen?: boolean;
  rtsProLoadBoardJoyRideSeen?: boolean;
  notificationMessage?: NotificationMessage;
  ryanTransportationOnboarding?: { loadboardAccessRequested: boolean };
}

interface Auth0Context {
  getAccessTokenSilently?: Auth0ContextInterface['getAccessTokenSilently'];
  isAuthenticated?: boolean;
  user?: Auth0ContextInterface['user'];
}

export const auth0Context: Auth0Context = {
  getAccessTokenSilently: undefined,
  isAuthenticated: false,
  user: undefined,
};

export const addAuth0Context = (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  isAuthenticated: boolean,
  user: Auth0ContextInterface['user']
) => {
  auth0Context.getAccessTokenSilently = getAccessTokenSilently;
  auth0Context.isAuthenticated = isAuthenticated;
  auth0Context.user = user;
};

export const getCurrentUser = () => {
  const currentUser = (LocalAuth.getUser() as any) ?? auth0Context.user;

  // isEmpty
  return (Object.keys(currentUser).length === 0 ? undefined : currentUser) as
    | CurrentUser
    | undefined;
};

export const getAuthToken = async () => {
  const token = LocalAuth.getTokenValue();
  if (token) return token;
  if (auth0Context.getAccessTokenSilently) {
    return await auth0Context.getAccessTokenSilently();
  }
};

const validateHasPermission = (permissionName: string): boolean => {
  const user = LocalAuth.getUser() ?? auth0Context.user;

  return !!(
    user?.permissions &&
    Array.isArray(user.permissions) &&
    user.permissions.some((x) => x.name === permissionName)
  );
};

// Freight - Book It Now role allows the user to view the load board, view prices, submit quotes and submit BINs
export const getHasBookItNowPermissions = () => {
  return validateHasPermission('ryantrans.LoadboardBook');
};

// Full freight access is represented by the Book It Now permission.
export const hasFreightFullAccess = () => {
  return validateHasPermission('ryantrans.LoadboardBook');
};

export const getHasLoadManagementPermission = () => {
  return validateHasPermission('loadManagement.User');
};

// Freight role only allows the user to view the load board
export const getHasFreightPermission = () => {
  return validateHasPermission('ryantrans.Loadboard');
};

// Freight with Rates role only allows the user to view the load board and view prices
export const getHasFreightRatesPermission = () => {
  return validateHasPermission('ryantrans.Loadprices');
};

// Freight Quoting role only allows the user to view the load board, view prices and submit quotes
export const getHasFreightQuotingPermission = () => {
  return validateHasPermission('ryantrans.Loadquoting');
};

const getUser = async (token: string, userName: string) => {
  const response = await fetch(`${USER_URL}user/user/` + userName, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return (await response.json()) as CurrentUser;
};

export const authenticateAndSaveUser = async (
  token: string,
  userName: string
) => {
  LocalAuth.setToken({ email: userName, token });

  const user = await getUser(token, userName);
  logFirebaseUser(user._id, user.user_type);
  LocalAuth.setUser(user as any);
};

export const isAuthenticated = () => {
  if (LocalAuth.getTokenValue() && LocalAuth.getUser()) {
    const user = LocalAuth.getUser() as unknown as CurrentUser;
    const isValid = user.permissions?.some(
      (x) => x.name === LOADBOARD_PERMISSION
    );

    if (!isValid) {
      return false;
    }
    logFirebaseUser(user?._id, user?.user_type);
    return true;
  }
  return false;
};

export const getUserEmail = () => {
  const user = getCurrentUser();
  if (user) {
    return user.email;
  } else return '';
};

export const redirectToLogout = () => {
  globalThis.location.href = `${RTSPRO_URL}/logout?finaldestination=${LOADBOARD_URL}`;
};

export const redirectToV2Logout = () => {
  globalThis.location.href = `${RTSPRO_URL}/v2/logout`;
};

export const hasBookItNowOrLoadBoardPermissions = (permissions: string[]) =>
  permissions.includes(BOOKIT_NOW_PERMISSION) ||
  permissions.includes(LOADBOARD_PERMISSION);

export const hasFullAccessPermissions = (permissions: string[]) =>
  permissions.includes(BOOKIT_NOW_PERMISSION);

export const getPersmissionsLevel = (companyPermissions: string[]): string => {
  if (companyPermissions.includes(BOOKIT_NOW_PERMISSION)) {
    return FREIGHT_FULL_ACCESS_PERMISSION_LEVEL;
  }

  return FREIGHT_VIEW_ACCESS_PERMISSION_LEVEL;
};
